import { useState } from "react";

type Problem = {
  scenario: string;
  question: string;
  choices: string[];  // index 0 is always correct; shuffled on mount
  explain: string;
};

const STARTER: Problem[] = [
  {
    scenario: "You're out to eat with your family. The bill is $24.00.",
    question: "You want to leave a 20% tip. How much is that?",
    choices: ["$4.80", "$2.40", "$6.00"],
    explain: "20% of $24 = $24 × 0.20 = $4.80. Quick trick: find 10% first ($2.40), then double it.",
  },
  {
    scenario: "A jacket is tagged at $60.00 and it's 25% off.",
    question: "What do you actually pay at the register?",
    choices: ["$45.00", "$15.00", "$40.00"],
    explain: "25% off means you pay 75%. $60 × 0.75 = $45. The $15 is the discount, not the price.",
  },
  {
    scenario: "You and 3 friends go out and the total bill is $52.00.",
    question: "You split it evenly. How much does each person owe?",
    choices: ["$13.00", "$15.00", "$12.00"],
    explain: "$52 ÷ 4 people = $13 each. Always divide by the number of people, not people minus one.",
  },
  {
    scenario: "You want to buy a bike that costs $120.00.",
    question: "If you save $5 a week, how many weeks until you can buy it?",
    choices: ["24 weeks", "20 weeks", "30 weeks"],
    explain: "$120 ÷ $5/week = 24 weeks. Saving consistently toward a goal really works.",
  },
  {
    scenario: "You received $75 for your birthday and spent $28 on a gift for a friend.",
    question: "How much do you have left?",
    choices: ["$47", "$42", "$53"],
    explain: "$75 − $28 = $47. Simple subtraction — but tracking this is the foundation of a budget.",
  },
  {
    scenario: "You babysit for 7 hours and get paid $10 per hour.",
    question: "How much do you earn in total?",
    choices: ["$70", "$65", "$77"],
    explain: "$10 × 7 hours = $70. Multiply your hourly rate by hours worked to find total earnings.",
  },
  {
    scenario: "You're buying 3 items that each cost $7.50.",
    question: "What's the total before tax?",
    choices: ["$22.50", "$21.00", "$25.00"],
    explain: "$7.50 × 3 = $22.50. Estimating totals before reaching the register saves surprises.",
  },
  {
    scenario: "Headphones are normally $35.00 but they're 10% off today.",
    question: "How much will you pay?",
    choices: ["$31.50", "$28.00", "$34.00"],
    explain: "10% of $35 = $3.50, so you pay $35 − $3.50 = $31.50.",
  },
  {
    scenario: "You earn $8 per hour and work 5 hours on Saturday.",
    question: "You put 50% of your earnings straight into savings. How much is that?",
    choices: ["$20", "$16", "$24"],
    explain: "$8 × 5 = $40 earned. 50% of $40 = $20 saved. Automating savings is a great habit.",
  },
  {
    scenario: "Three friends each chip in $15 to buy a birthday present.",
    question: "How much do they have to spend on the gift?",
    choices: ["$45", "$40", "$50"],
    explain: "$15 × 3 = $45. Pooling money with friends lets you buy something nicer than any one could alone.",
  },
];

const BUILDER: Problem[] = [
  {
    scenario: "You earn $320 per week at your part-time job.",
    question: "You decide to save 25% of every paycheck. How much do you save each week?",
    choices: ["$80", "$64", "$96"],
    explain: "$320 × 0.25 = $80/week. Paying yourself first — before spending — is the core savings habit.",
  },
  {
    scenario: "You borrow $200 at 15% simple interest for one year.",
    question: "How much do you owe back at the end of the year?",
    choices: ["$230", "$215", "$245"],
    explain: "Interest = $200 × 0.15 = $30. Total owed = $200 + $30 = $230. Always know the true cost of borrowing.",
  },
  {
    scenario: "You buy a $5 coffee 3 times every week.",
    question: "How much does that habit cost you in a full year?",
    choices: ["$780", "$540", "$936"],
    explain: "$5 × 3 days × 52 weeks = $780/year. Small daily habits become big annual numbers.",
  },
  {
    scenario: "Your first job pays $800 gross per month. About 20% goes to taxes.",
    question: "What's your actual monthly take-home pay?",
    choices: ["$640", "$600", "$680"],
    explain: "$800 × 0.20 = $160 in taxes. $800 − $160 = $640 take-home. Gross ≠ what you spend.",
  },
  {
    scenario: "You have $400 saved and your goal is $1,000.",
    question: "You save $75 per month. How many more months until you hit your goal?",
    choices: ["8 months", "6 months", "10 months"],
    explain: "You need $600 more. $600 ÷ $75/month = 8 months exactly.",
  },
  {
    scenario: "A phone costs $800. The store offers: $200 down + 12 monthly payments of $55.",
    question: "What's the total amount you actually pay?",
    choices: ["$860", "$800", "$920"],
    explain: "$200 + ($55 × 12) = $200 + $660 = $860. Payment plans often cost more than the sticker price.",
  },
  {
    scenario: "You work 15 hours per week at $12 per hour for 8 weeks over summer.",
    question: "What are your total summer earnings?",
    choices: ["$1,440", "$1,200", "$1,680"],
    explain: "$12 × 15 hours × 8 weeks = $1,440. Knowing your earning potential helps you plan ahead.",
  },
  {
    scenario: "A restaurant bill is $67.50 and that already includes 8% sales tax.",
    question: "What was the price before tax was added?",
    choices: ["$62.50", "$59.50", "$65.50"],
    explain: "$67.50 ÷ 1.08 = $62.50. To remove tax, divide by (1 + tax rate), don't just subtract 8%.",
  },
  {
    scenario: "You earn $1,500 per month and want to save 20% of your income.",
    question: "How much will you have saved after one full year?",
    choices: ["$3,600", "$3,000", "$4,200"],
    explain: "$1,500 × 0.20 = $300/month. $300 × 12 months = $3,600/year.",
  },
  {
    scenario: "You take out a $1,000 personal loan at 12% simple annual interest.",
    question: "After 6 months, how much interest have you accumulated?",
    choices: ["$60", "$120", "$45"],
    explain: "Annual interest = $1,000 × 0.12 = $120. Half a year = $60. Interest accrues even when you're not watching.",
  },
];

const MASTER: Problem[] = [
  {
    scenario: "You invest $1,000 and it earns a 7% annual return, compounded yearly.",
    question: "After 10 years, roughly how much is it worth?",
    choices: ["~$1,967", "~$1,400", "~$2,500"],
    explain: "$1,000 × 1.07¹⁰ ≈ $1,967. This is the power of compound growth — you earn returns on your returns.",
  },
  {
    scenario: "Your hourly wage is $15.00 and your effective tax rate is 22%.",
    question: "What's your real take-home pay per hour after taxes?",
    choices: ["$11.70", "$10.50", "$13.20"],
    explain: "$15 × (1 − 0.22) = $15 × 0.78 = $11.70. Every dollar you earn has a real after-tax value.",
  },
  {
    scenario: "You have an $800 credit card balance at 24% APR.",
    question: "If you make no payment this month, how much interest is added?",
    choices: ["$16", "$24", "$8"],
    explain: "Monthly rate = 24% ÷ 12 = 2%. $800 × 0.02 = $16. APR ÷ 12 gives you the monthly rate.",
  },
  {
    scenario: "Your monthly take-home is $2,400. You follow the 50/30/20 budgeting rule.",
    question: "How much does the rule say you should save or invest each month?",
    choices: ["$480", "$360", "$600"],
    explain: "20% of $2,400 = $480 for savings/investing. The other 50% covers needs, 30% goes to wants.",
  },
  {
    scenario: "You carry $3,500 in credit card debt at 20% APR and make zero payments for a year.",
    question: "Approximately how much will you owe after 12 months?",
    choices: ["$4,200", "$3,700", "$4,500"],
    explain: "$3,500 × 1.20 = $4,200. Compound interest on debt snowballs — the same math that builds wealth destroys it here.",
  },
  {
    scenario: "Your salary is $50,000/year. Your effective tax rate is 25%.",
    question: "What's your monthly take-home pay?",
    choices: ["$3,125", "$2,917", "$3,500"],
    explain: "$50,000 × 0.75 = $37,500 after tax. $37,500 ÷ 12 = $3,125/month.",
  },
  {
    scenario: "You earn $18/hour and qualify for time-and-a-half overtime pay.",
    question: "You work 8 hours of overtime this week. How much do you earn for just those hours?",
    choices: ["$216", "$144", "$252"],
    explain: "Time-and-a-half = $18 × 1.5 = $27/hour. $27 × 8 = $216 in overtime pay.",
  },
  {
    scenario: "Your rent is $1,200 per month.",
    question: "Using the 30% housing rule, what's the minimum monthly income you should be earning?",
    choices: ["$4,000", "$3,200", "$5,000"],
    explain: "$1,200 ÷ 0.30 = $4,000. Spending more than 30% of income on housing puts pressure on everything else.",
  },
  {
    scenario: "You earn $22/hour and work 40 hours/week for 50 weeks of the year.",
    question: "After a 24% tax rate, what's your annual take-home pay?",
    choices: ["$33,440", "$44,000", "$28,600"],
    explain: "Gross = $22 × 40 × 50 = $44,000. After 24% tax: $44,000 × 0.76 = $33,440.",
  },
  {
    scenario: "You want to retire with $1,000,000. You're 20 years old and invest $300/month at 8% annual return.",
    question: "Roughly how old will you be when you hit $1 million?",
    choices: ["~58 years old", "~65 years old", "~48 years old"],
    explain: "At $300/month and 8% return, you hit ~$1M in about 38 years — around age 58. Starting early is everything.",
  },
];

const STREAK_FIRE = (s: number) => s >= 5 ? "🔥🔥🔥" : s >= 3 ? "🔥🔥" : s >= 2 ? "🔥" : "";

export default function MathGame({ onComplete, color, dark, tier = "builder" }: any) {
  const POOL = tier === "starter" ? STARTER : tier === "master" ? MASTER : BUILDER;

  const [problems] = useState(() =>
    [...POOL]
      .sort(() => Math.random() - 0.5)
      .slice(0, 6)
      .map(p => ({ ...p, shuffled: [...p.choices].sort(() => Math.random() - 0.5) }))
  );

  const [idx,    setIdx]    = useState(0);
  const [score,  setScore]  = useState(0);
  const [streak, setStreak] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [done,   setDone]   = useState(false);

  const cur = problems[idx];

  function pick(choice: string) {
    if (picked !== null) return;
    setPicked(choice);
    const ok = choice === cur.choices[0];
    if (ok) { setScore(s => s + 1); setStreak(s => s + 1); }
    else    { setStreak(0); }
    setTimeout(() => {
      setPicked(null);
      if (idx + 1 >= problems.length) { setDone(true); setTimeout(() => onComplete(score + (ok ? 1 : 0)), 1000); }
      else setIdx(i => i + 1);
    }, 1800);
  }

  const fire = STREAK_FIRE(streak);
  const isRevealed = picked !== null;

  return (
    <div style={{ fontFamily: "Nunito" }}>
      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ background: `linear-gradient(135deg,${color},${dark})`, borderRadius: 12, padding: "4px 12px" }}>
          <span style={{ fontSize: 15, fontWeight: 900, color: "#fff" }}>⭐ {score}/{problems.length}</span>
        </div>
        {fire
          ? <span style={{ fontSize: 18, fontWeight: 900, color: "#f97316" }}>{fire} ×{streak}</span>
          : <span />}
        <span style={{ fontSize: 12, fontWeight: 800, color: "#9ca3af" }}>{idx + 1}/{problems.length}</span>
      </div>

      {/* Progress bar */}
      <div style={{ display: "flex", gap: 5, marginBottom: 14 }}>
        {problems.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 6, borderRadius: 99,
            background: i < idx ? color : i === idx ? dark : "#e5e7eb",
            transition: "background 0.3s",
          }} />
        ))}
      </div>

      {!done ? (
        <>
          {/* Scenario card */}
          <div style={{
            background: `linear-gradient(135deg,${color}20,${color}08)`,
            border: `2.5px solid ${color}55`, borderRadius: 22,
            padding: "18px 16px", marginBottom: 14,
            boxShadow: `0 6px 24px ${color}28`,
          }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: dark, textTransform: "uppercase", letterSpacing: 2, marginBottom: 8 }}>
              Scenario
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#4b5563", lineHeight: 1.5, marginBottom: 12 }}>
              {cur.scenario}
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#1f2937", lineHeight: 1.4 }}>
              {cur.question}
            </div>
          </div>

          {/* Explanation (shown after pick) */}
          {isRevealed && (
            <div style={{
              background: picked === cur.choices[0] ? "#d1fae5" : "#fee2e2",
              border: `2px solid ${picked === cur.choices[0] ? "#10b981" : "#ef4444"}`,
              borderRadius: 14, padding: "10px 14px", marginBottom: 14,
              fontSize: 13, fontWeight: 700,
              color: picked === cur.choices[0] ? "#065f46" : "#991b1b",
              lineHeight: 1.4, animation: "pop 0.3s",
            }}>
              {picked === cur.choices[0] ? "✅ " : "❌ "}{cur.explain}
            </div>
          )}

          {/* Answer choices */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {cur.shuffled.map((choice, i) => {
              const isSelected = isRevealed && picked === choice;
              const isCorrect  = choice === cur.choices[0];
              const isWrong    = isRevealed && isSelected && !isCorrect;
              const highlight  = isRevealed && isCorrect;

              return (
                <button
                  key={i}
                  onClick={() => pick(choice)}
                  className="btn-bounce"
                  style={{
                    background: highlight
                      ? "linear-gradient(135deg,#d1fae5,#a7f3d0)"
                      : isWrong
                      ? "linear-gradient(135deg,#fee2e2,#fecaca)"
                      : "#fff",
                    border: `2.5px solid ${highlight ? "#10b981" : isWrong ? "#ef4444" : color + "55"}`,
                    borderRadius: 16, padding: "14px 16px",
                    fontSize: 16, fontWeight: 900,
                    color: highlight ? "#065f46" : isWrong ? "#991b1b" : dark,
                    cursor: isRevealed ? "default" : "pointer",
                    transition: "all 0.2s", textAlign: "left",
                    opacity: isRevealed && !highlight && !isWrong ? 0.4 : 1,
                    boxShadow: highlight
                      ? "0 4px 14px #10b98144"
                      : isWrong
                      ? "0 4px 14px #ef444444"
                      : `0 2px 8px ${color}22`,
                  }}
                >
                  {choice}
                  {highlight && " ✅"}
                  {isWrong && " ❌"}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0", animation: "pop 0.4s" }}>
          <div style={{ fontSize: 56 }}>{score >= problems.length ? "🏆" : score >= 4 ? "⭐" : "📚"}</div>
          <div style={{ fontSize: 22, fontWeight: 900, color, marginTop: 8 }}>
            {score >= problems.length ? "Money math pro!" : score >= 4 ? "Solid work!" : "Keep practicing!"}
          </div>
          <div style={{ fontSize: 32, fontWeight: 900, color: dark, marginTop: 4 }}>{score}/{problems.length}</div>
          <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700, marginTop: 6 }}>
            These are the calculations that actually matter in real life.
          </div>
        </div>
      )}
    </div>
  );
}
